import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment-timezone'
import 'moment/locale/en-gb'
import {
  Card,
  ContentSectionBlock,
  Col,
  FormInput,
  FormInputDateRange,
  FormInputSelect,
  Row
} from 'camino-react'

import { cloudhelpGet } from 'Utilities/CloudHelp'
import ReportAccordion from 'Components/Reports/Surveys/ReportAccordion'
import LoadingSpinner from 'Components/LoadingSpinner'
import Alert from 'Components/Alert'

const AnswerList = ({ answers }) => {
  const answersListItems = _.map(answers, (answer, question) => (
    <li>
      {question}
      :
      {' '}
      {answer}
    </li>
  ))

  return (
    <ul>
      {answersListItems}
    </ul>
  )
}

class SurveysReport extends React.Component {
  constructor (props) {
    super(props)
    this.allQueuesOption = {
      name: 'All queues',
      id: 0,
      value: 0,
      label: 'All queues'
    }

    this.state = {
      surveyResponses: props.surveyResponses,
      chosenQueue: this.allQueuesOption,
      queues: [],
      users: [],
      filteredUsers: [],
      dateFrom: props.activeFilters.start_time,
      dateTo: props.activeFilters.end_time,
      loading: true,
      alert: {
        show: false,
        type: 'info',
        text: ''
      }
    }

    this.resetFilters = this.resetFilters.bind(this)
    this.getQueues = this.getQueues.bind(this)
    this.changeQueueFilter = this.changeQueueFilter.bind(this)
    this.changeDateFilter = this.changeDateFilter.bind(this)
  }

  componentDidMount () {
    this.getQueues()
  }

  getQueues () {
    axios.get('/api/ticket_queues')
      .then((response) => {
        const { usersAverages } = this.props
        const queues = response.data
        const users = {}

        _.each(queues, (queue) => {
          users[queue.id] = _.map(
            queue.users, (user) => {
              const averageScoreObject = _.find(usersAverages, { user_id: user.id })

              return (
                Object.assign(
                  {},
                  user,
                  { averageScore: _.isNil(averageScoreObject) ? null : averageScoreObject.score }
                )
              )
            }
          )
        })

        this.setState({
          queues: _.sortBy(queues, queue => (queue.name)),
          users,
          loading: false
        })
      })
      .catch(() => {
        this.setState({
          loading: false,
          alert: {
            show: true,
            type: 'error',
            text: 'There was an issue retrieving queues for the filters.'
          }
        })
      })
  }

  changeDateFilter (dates) {
    const { surveyReportPath } = this.props
    const { dateFrom, dateTo } = this.state

    if (!_.every(dates, date => _.some(
      [dateFrom, dateTo], stateDate => stateDate.toLocaleString() === date.toLocaleString()
    ))) {
      if (_.compact(dates).length === 2) {
        const getParams = { report: { start_time: dates[0], end_time: dates[1] } }
        this.setState({ loading: true })

        cloudhelpGet(surveyReportPath, getParams)
          .then((response) => {
            this.setState({
              dateFrom: dates[0],
              dateTo: dates[1],
              surveyResponses: response.data.rows,
              loading: false
            })
          })
      }
    }
  }

  changeQueueFilter (queueId) {
    const { allQueuesOption } = this
    const { users, queues } = this.state

    const chosenQueue =
      queueId === 0 ? allQueuesOption : _.find(queues, queue => queueId === queue.id)

    this.setState({
      chosenQueue: {
        id: chosenQueue.id,
        name: chosenQueue.name,
        value: chosenQueue.id,
        label: chosenQueue.name
      },
      filteredUsers: queueId === 0 ? [] : users[queueId]
    })
  }

  resetFilters () {
    const { activeFilters, surveyResponses } = this.props

    this.setState({
      dateFrom: activeFilters.start_time,
      dateTo: activeFilters.end_time,
      chosenQueue: this.allQueuesOption,
      surveyResponses
    })
  }

  render () {
    const {
      surveyResponses,
      chosenQueue,
      queues,
      filteredUsers,
      dateFrom,
      dateTo,
      loading,
      alert,
      users
    } = this.state

    const {
      resetFilters,
      changeQueueFilter,
      changeDateFilter
    } = this

    const setUserName = (surveyResponse) => {
      const surveyResponseOwner = _.find(
        _.flatMap(users, (queueUsers, queueId) => queueUsers),
        user => user.id === surveyResponse.owner_id
      )

      if (_.isNil(surveyResponseOwner)) {
        surveyResponse.owner_name = ' Not assigned' // Starts with a blank space to put it at the top of name sorting
      } else {
        surveyResponse.owner_name = surveyResponseOwner.name
      }
    }

    const queueOptions = _.flatMap(
      _.concat([this.allQueuesOption], queues),
      t => ({ label: t.name, value: t.id })
    )

    const userSurveyResponses = _.filter(surveyResponses, surveyResponse => _.includes(
      _.map(filteredUsers, 'id'),
      surveyResponse.owner_id
    ))

    _.each(surveyResponses, (surveyResponse) => {
      const surveyResponseQueue = _.find(
        queues,
        queue => queue.id === surveyResponse.queue_id
      )

      if (!_.isNil(surveyResponseQueue)) {
        surveyResponse.queue_name = surveyResponseQueue.name
      }
      setUserName(surveyResponse)
    })

    _.each(userSurveyResponses, surveyResponse => setUserName(surveyResponse))

    const allGroupedQueues = _.groupBy(
      surveyResponses, survey => (_.isNil(survey.queue_name) ? 'No Queue' : survey.queue_name)
    )
    const currentSurveysByQueue = { [chosenQueue.label]: allGroupedQueues[chosenQueue.label] }

    const userColumns = [
      {
        Header: 'Ticket',
        accessor: 'ticket_id',
        maxWidth: '150',
        Cell: row => (
          <a className="link link--decorated link--initial-size link--thick" href={row.original.href}>
            {`Ticket #${row.value}`}
          </a>
        )
      },
      { Header: 'Taken by', accessor: 'respondee_email', maxWidth: '200' },
      { Header: 'Answers', accessor: 'answers', Cell: row => (<AnswerList answers={row.value} />) },
      { Header: 'Score', accessor: 'humanized_score', maxWidth: '150' },
      { Header: 'Score percentage', accessor: 'humanized_score_percentage', maxWidth: '150' }
    ]

    const queueColumns = _.flatten([{ Header: 'Assigned to', accessor: 'owner_name', maxWidth: '200' }, userColumns])
    const accordionDetails = {
      columns: chosenQueue.id === 0 ? queueColumns : userColumns,
      groupedSurveys: chosenQueue.id === 0 ? allGroupedQueues : currentSurveysByQueue
    }
    const chosenQueueIsEmpty = chosenQueue.id !== 0 && !(chosenQueue.label in allGroupedQueues)
    const accordion = () => {
      if (loading) {
        return <LoadingSpinner />
      }

      if (_.isEmpty(accordionDetails.groupedSurveys) || chosenQueueIsEmpty) {
        return (
          <p>
            There are no survey responses for the chosen filters.
          </p>
        )
      }

      // Users are currently stored in groups of their queues for other reporting reasons
      // This is just to get a full array of users for filtering their scores below
      const userArray = []
      _.each(users, userGroup => userArray.push(userGroup))

      return (
        <ReportAccordion
          surveyGroups={accordionDetails.groupedSurveys}
          filteredUsers={[]}
        />
      )
    }

    return (
      <>
        <Card padding={false} allowOverflow>
          <ContentSectionBlock
            title="Filters"
            actions={[
              {
                onClick: (e) => { resetFilters(e) },
                text: 'Reset'
              }
            ]}
          >
            <Row classExtra="thin-gutters">
              <Col>
                <FormInput label="Queue">
                  <FormInputSelect
                    value={chosenQueue}
                    onValue={queueId => changeQueueFilter(queueId)}
                    options={queueOptions}
                  />
                </FormInput>
              </Col>

              <Col>
                <FormInput label="Date Range">
                  <FormInputDateRange
                    value={[new Date(dateFrom), new Date(dateTo)]}
                    onValue={value => changeDateFilter(value)}
                  />
                </FormInput>
              </Col>
            </Row>
          </ContentSectionBlock>
          <ContentSectionBlock title={`${chosenQueue.name} survey report`}>
            <Row>
              <Col>
                <Alert text={alert.text} alertType={alert.type} showAlert={alert.show} />
              </Col>
            </Row>

            <Row>
              <Col>
                {accordion()}
              </Col>
            </Row>
          </ContentSectionBlock>
        </Card>
      </>
    )
  }
}

SurveysReport.propTypes = {
  surveyResponses: PropTypes.arrayOf(PropTypes.object).isRequired,
  surveyReportPath: PropTypes.string.isRequired,
  activeFilters: PropTypes.shape({
    start_time: PropTypes.string,
    end_time: PropTypes.string
  }),
  usersAverages: PropTypes.arrayOf(
    PropTypes.shape({}).isRequired
  ).isRequired
}

SurveysReport.defaultProps = {
  activeFilters: {
    start_time: new Date(),
    end_time: new Date()
  }
}

AnswerList.propTypes = {
  answers: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default SurveysReport
